import React, { useMemo, useState } from 'react';
import { produce } from 'immer';
import classNames from 'classnames';

import services from 'services/services';
import service_studies from 'services/studies';
import helpers from 'utils/helpers';
import { HeaderSubnavButton } from 'components/Header/HeaderSubnavButton';
import NiceModal from 'components/NiceModal';
import { COL_FIRST_NAME, COL_LAST_NAME, COL_EMAIL, STUDY_TABLE_CHECKED_STATUS } from 'utils/constants';
import { SessionDatepickerModal } from 'components/SessionDatepickerModal';
import { EmailCcTokenizer } from 'components/EmailCcTokenizer';
import { useQuery } from 'hooks/useQuery';
import { TableV2 } from 'components/Table';
import reviewRecipientsModalStyles from 'styles/review-recipients-modal.module.css';
import NumberedPagination from 'components/NumberedPagination';
import { useDebounceValue } from 'hooks/useDebounceValue';
import { Skeleton } from 'components/Skeleton';
import { ProtectedField, ProtectedFieldComposite } from 'components/ProtectedField';

/**
 * Renders the ReviewRecipientsModal component which displays a modal for reviewing recipients of a study.
 * It includes search functionality, pagination, and displays recipient data based on the provided filters and status.
 *
 * @param {Object} props - The parameters for the modal component.
 * @param {boolean} props.isOpen - Specifies whether the modal is open or not.
 * @param {() => void} props.onClose - Callback function invoked when the modal is closed.
 * @param {number} props.studyId - The ID of the study for which the recipients are being reviewed.
 * @param {string} props.[checkAllOrVisibleStatus] - Defines the status to determine if all or visible items are checked. Possible values are 'all' or 'custom_count'.
 * @param {Array<{ id: number }>} props.[checkedPeople] - An array of objects representing the people currently checked in the selection.
 * @param {{
 *     searchKeyword: string,
 *     filters: Object,
 * }} props.filters - Filters object containing applied filters and search keywords.
 *
 * @return {JSX.Element|null} A modal component for reviewing recipients of a study or null if loading custom data keys.
 */
export function ReviewRecipientsModal({ isOpen, onClose, studyId, checkAllOrVisibleStatus, checkedPeople, filters }) {
    const [pagination, setPagination] = useState({
        page: 1,
        perPage: 20
    });
    const peopleIds = useMemo(() => checkedPeople?.map(person => person.id) || [], [checkedPeople]);
    const { data: customDataKeys, isLoading: isCustomDataKeysLoading } = useQuery({
        queryFn: () => services.getCustomDataColumnsXHR()
    });
    const { data, isLoading: isDataLoading } = useQuery({
        queryFn: () => {
            const options = {
                pagination_limit: pagination.perPage,
                page: pagination.page,
                study_id: studyId
            };

            if (checkAllOrVisibleStatus === STUDY_TABLE_CHECKED_STATUS.ALL) {
                options.search_keyword = filters.searchKeyword;
                options.filters = JSON.stringify(filters.filters);
            } else {
                options.peopleIds = peopleIds;
            }

            return service_studies.getStudyPeople(options);
        },
        variables: {
            ...pagination
        },
        skip: !isOpen || checkAllOrVisibleStatus === STUDY_TABLE_CHECKED_STATUS.CUSTOM
    });
    const { firstNameCustomDataKey, lastNameCustomDataKey, emailCustomDataKey } = useMemo(() => {
        if (!customDataKeys) return {};

        const firstNameCustomDataKey = helpers.getPersonDataKeyByTitle(customDataKeys, COL_FIRST_NAME);
        const lastNameCustomDataKey = helpers.getPersonDataKeyByTitle(customDataKeys, COL_LAST_NAME);
        const emailCustomDataKey = helpers.getPersonDataKeyByTitle(customDataKeys, COL_EMAIL);

        return {
            firstNameCustomDataKey,
            lastNameCustomDataKey,
            emailCustomDataKey
        };
    }, [customDataKeys]);

    const changePage = page => {
        setPagination(
            produce(draft => {
                draft.page = page;
            })
        );
    };

    const changePerPage = perPage => {
        setPagination(
            produce(draft => {
                draft.page = 1;
                draft.perPage = perPage;
            })
        );
    };

    if (isCustomDataKeysLoading) return null;

    return (
        <NiceModal
            isOpen={isOpen}
            shouldCloseOnOverlayClick
            onRequestClose={onClose}
            title="Recipient Preview"
            style={{ content: { width: '1200px' } }}
        >
            <p className="fs-title-14 mb-4 mt-0">Total {data?.total} recipients</p>
            {!!data?.data.length && (
                <TableV2
                    className={classNames(reviewRecipientsModalStyles['no-head'], 'mb-8 !-mx-8')}
                    style={{ maxHeight: '400px', height: '400px', overflowY: 'auto' }}
                    data={data?.data || []}
                    columns={[
                        {
                            render: record => (
                                <ProtectedFieldComposite
                                    person={record.person}
                                    hideable
                                    fields={[
                                        {
                                            dataKey: COL_FIRST_NAME,
                                            hideable: true,
                                            labels: { unmask: 'Unmask first name' },
                                            empty: '-'
                                        },
                                        {
                                            dataKey: COL_LAST_NAME,
                                            hideable: true,
                                            labels: { unmask: 'Unmask last name' },
                                            empty: '-'
                                        }
                                    ]}
                                />
                            ),
                            onCell: () => ({
                                className: 'nowrap fs-accent-14 color-text-primary'
                            }),
                            key: 'full-name'
                        },
                        {
                            render: record => (
                                <ProtectedField
                                    person={record.person}
                                    dataKey={COL_EMAIL}
                                    hideable
                                    labels={{ unmask: 'Unmask email' }}
                                    empty="-"
                                />
                            ),
                            key: 'email'
                        }
                    ]}
                />
            )}
            {!data?.data.length && isDataLoading && <Skeleton height={400} />}
            {!data?.data.length && !isDataLoading && (
                <div className="flex items-center justify-center" style={{ height: '400px' }}>
                    <p>No data</p>
                </div>
            )}
            <NumberedPagination
                total={data?.total || 0}
                page={pagination.page}
                perPage={pagination.perPage}
                onPageChange={changePage}
                onPerPageChange={changePerPage}
                from={data?.from || 0}
                links={data?.links || []}
                to={data?.to || 0}
                className="!-mx-8 !-mb-8"
            />
        </NiceModal>
    );
}
